// ═══════════════════════════════════════════════════════════════════════════════
// Step 2 — Behavioural Assessment Questionnaire
// Scoring Engine
// ═══════════════════════════════════════════════════════════════════════════════

export type Emotion = 'Fear' | 'Greed' | 'Ego' | 'Impatience' | 'Hope';

export const ALL_EMOTIONS: Emotion[] = ['Fear', 'Greed', 'Ego', 'Impatience', 'Hope'];

// ─── Answer Signal Map ─────────────────────────────────────────────────────────
// Each MC answer maps to 0-2 signals for specific emotions.
// The trader NEVER sees these mappings.

export interface AnswerOption {
  value: string; // A, B, C, D, E
  label: string;
  signals: Partial<Record<Emotion, number>>; // emotion → signal count
}

export interface MCQuestion {
  id: number;
  section: number;
  type: 'mc';
  question: string;
  options: AnswerOption[];
}

export interface OpenQuestion {
  id: number;
  section: number;
  type: 'open';
  question: string;
}

export interface ScaleQuestion {
  id: number;
  section: number;
  type: 'scale';
  question: string;
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
}

export interface SelectQuestion {
  id: number;
  section: number;
  type: 'select';
  question: string;
  options: string[];
}

export type Question = MCQuestion | OpenQuestion | ScaleQuestion | SelectQuestion;

// ─── Section Definitions ───────────────────────────────────────────────────────
export const SECTIONS: { id: number; title: string; goal: string }[] = [
  { id: 1, title: 'Self-Assessment', goal: 'Let the trader tell us their problem before we diagnose anything' },
  { id: 2, title: 'Pattern Recognition', goal: 'Identify repeating patterns the trader may or may not be aware of' },
  { id: 3, title: 'Emotional Response', goal: 'Observe how they react to specific trading scenarios' },
  { id: 4, title: 'Decision Making', goal: 'Understand their decision-making process under different conditions' },
  { id: 5, title: 'External Factors', goal: 'Check if external influences affect trading' },
  { id: 6, title: 'Core Belief', goal: 'Understand the trader\'s self-awareness level' },
];

// ─── Questions ─────────────────────────────────────────────────────────────────
export const QUESTIONS: Question[] = [
  // SECTION 1
  {
    id: 1, section: 1, type: 'open',
    question: 'In your own words, what is the single biggest problem you face in your trading?',
  },
  {
    id: 2, section: 1, type: 'select',
    question: 'How many years have you been trading?',
    options: ['Less than 1 year', '1–3 years', '3–5 years', '5–10 years', '10+ years'],
  },
  {
    id: 3, section: 1, type: 'scale',
    question: 'On a scale of 1–10, how would you rate your current trading consistency?',
    min: 1, max: 10, minLabel: '1 — Very Inconsistent', maxLabel: '10 — Highly Consistent',
  },

  // SECTION 2
  {
    id: 4, section: 2, type: 'open',
    question: 'Describe a recent trade that went wrong. What happened and what did you do?',
  },
  {
    id: 5, section: 2, type: 'open',
    question: 'When you look back at your worst trades, what pattern do you see repeating?',
  },
  {
    id: 6, section: 2, type: 'open',
    question: 'What trading rule do you break most often?',
  },

  // SECTION 3
  {
    id: 7, section: 3, type: 'mc',
    question: 'What happens emotionally when you hit 3 losing trades in a row?',
    options: [
      { value: 'A', label: 'I stop trading and review', signals: {} },
      { value: 'B', label: 'I feel anxious but continue carefully', signals: { Fear: 1 } },
      { value: 'C', label: 'I get frustrated and want to recover', signals: { Ego: 1, Greed: 1 } },
      { value: 'D', label: 'I increase position size to win back losses', signals: { Ego: 2, Greed: 2 } },
      { value: 'E', label: 'I lose control and overtrade', signals: { Impatience: 2, Greed: 2 } },
    ],
  },
  {
    id: 8, section: 3, type: 'mc',
    question: 'What do you do immediately after a big win?',
    options: [
      { value: 'A', label: 'Take profit and stop for the day', signals: {} },
      { value: 'B', label: 'Feel confident and look for more trades', signals: { Greed: 1 } },
      { value: 'C', label: 'Increase my position size on the next trade', signals: { Greed: 2 } },
      { value: 'D', label: 'Feel invincible and trade more aggressively', signals: { Greed: 2, Ego: 1 } },
      { value: 'E', label: 'Worry about losing the gains', signals: { Fear: 2 } },
    ],
  },
  {
    id: 9, section: 3, type: 'mc',
    question: 'When a trade goes against you, at what point do you typically exit?',
    options: [
      { value: 'A', label: 'At my predetermined stop loss', signals: {} },
      { value: 'B', label: 'I move my stop loss further hoping it recovers', signals: { Hope: 2, Ego: 1 } },
      { value: 'C', label: 'I add to the position / average down', signals: { Hope: 2, Greed: 1 } },
      { value: 'D', label: 'I hold until I can\'t take it anymore', signals: { Fear: 2, Hope: 2 } },
      { value: 'E', label: 'I close it then immediately re-enter', signals: { Ego: 2, Impatience: 2 } },
    ],
  },
  {
    id: 10, section: 3, type: 'mc',
    question: 'How do you feel when you miss a good trade setup?',
    options: [
      { value: 'A', label: 'Accept it and wait for the next one', signals: {} },
      { value: 'B', label: 'Feel slight regret but move on', signals: {} },
      { value: 'C', label: 'Chase the move and enter late', signals: { Impatience: 2, Greed: 1 } },
      { value: 'D', label: 'Feel angry at myself for hesitating', signals: { Ego: 2 } },
      { value: 'E', label: 'It ruins my focus for the rest of the day', signals: { Fear: 2, Ego: 1 } },
    ],
  },

  // SECTION 4
  {
    id: 11, section: 4, type: 'mc',
    question: 'When you see a valid setup, how quickly do you act?',
    options: [
      { value: 'A', label: 'I enter immediately without hesitation', signals: {} },
      { value: 'B', label: 'I enter after brief confirmation', signals: {} },
      { value: 'C', label: 'I hesitate and sometimes miss it', signals: { Fear: 1 } },
      { value: 'D', label: 'I overthink and usually miss it', signals: { Fear: 2 } },
      { value: 'E', label: 'I need someone else to confirm before I act', signals: { Fear: 2 } },
    ],
  },
  {
    id: 12, section: 4, type: 'mc',
    question: 'On slow market days with no setups, what do you do?',
    options: [
      { value: 'A', label: 'Nothing — I wait patiently', signals: {} },
      { value: 'B', label: 'I review charts and study', signals: {} },
      { value: 'C', label: 'I feel restless but stay disciplined', signals: { Impatience: 1 } },
      { value: 'D', label: 'I look for setups in other pairs/markets', signals: { Impatience: 2 } },
      { value: 'E', label: 'I force trades to feel productive', signals: { Impatience: 2, Greed: 1 } },
    ],
  },
  {
    id: 13, section: 4, type: 'mc',
    question: 'How often do you deviate from your trading plan?',
    options: [
      { value: 'A', label: 'Rarely — I follow it strictly', signals: {} },
      { value: 'B', label: 'Occasionally — when I have strong conviction', signals: { Ego: 1 } },
      { value: 'C', label: 'Regularly — I adjust based on gut feeling', signals: { Impatience: 1, Ego: 1 } },
      { value: 'D', label: 'Often — my plan is more of a guideline', signals: { Impatience: 2 } },
      { value: 'E', label: 'I don\'t have a consistent plan', signals: { Impatience: 2 } },
    ],
  },

  // SECTION 5
  {
    id: 14, section: 5, type: 'mc',
    question: 'Does your trading performance change after personal conflicts (family, work)?',
    options: [
      { value: 'A', label: 'No — I separate trading from personal life', signals: {} },
      { value: 'B', label: 'Slightly — I\'m a bit more cautious', signals: { Fear: 1 } },
      { value: 'C', label: 'Sometimes — depends on severity', signals: { Fear: 1 } },
      { value: 'D', label: 'Yes — it noticeably affects my decisions', signals: { Fear: 2 } },
      { value: 'E', label: 'Significantly — I shouldn\'t trade but I do anyway', signals: { Fear: 2, Impatience: 1 } },
    ],
  },
  {
    id: 15, section: 5, type: 'mc',
    question: 'Do other people\'s opinions about your trading affect your confidence?',
    options: [
      { value: 'A', label: 'Not at all', signals: {} },
      { value: 'B', label: 'Slightly', signals: { Fear: 1 } },
      { value: 'C', label: 'Moderately', signals: { Fear: 1, Ego: 1 } },
      { value: 'D', label: 'Significantly', signals: { Fear: 2, Ego: 1 } },
      { value: 'E', label: 'I rely heavily on others\' validation', signals: { Fear: 2, Ego: 2 } },
    ],
  },

  // SECTION 6
  {
    id: 16, section: 6, type: 'mc',
    question: 'Do you believe your trading problems come from knowledge gaps or emotional reactions?',
    options: [
      { value: 'A', label: 'Mostly knowledge — I need better strategy', signals: {} },
      { value: 'B', label: 'More knowledge than emotion', signals: {} },
      { value: 'C', label: '50/50 — both equally', signals: {} },
      { value: 'D', label: 'More emotion than knowledge', signals: {} },
      { value: 'E', label: 'Mostly emotion — I know what to do but can\'t do it', signals: {} },
    ],
  },
  {
    id: 17, section: 6, type: 'open',
    question: 'What do you think is the hardest thing about becoming a consistently profitable trader?',
  },
  {
    id: 18, section: 6, type: 'open',
    question: 'If you could fix ONE thing about your trading behaviour, what would it be?',
  },
];

// ─── Max Possible Signals Per Emotion ─────────────────────────────────────────
// Used to normalise to 0–100%
// Count the max signals each emotion can accumulate across all MC questions
function computeMaxSignals(): Record<Emotion, number> {
  const maxMap: Record<Emotion, number> = { Fear: 0, Greed: 0, Ego: 0, Impatience: 0, Hope: 0 };
  QUESTIONS.forEach(q => {
    if (q.type !== 'mc') return;
    const mcQ = q as MCQuestion;
    // For each question, find the max signal per emotion across all options
    const perEmotion: Record<Emotion, number> = { Fear: 0, Greed: 0, Ego: 0, Impatience: 0, Hope: 0 };
    mcQ.options.forEach(opt => {
      ALL_EMOTIONS.forEach(em => {
        const sig = opt.signals[em] ?? 0;
        if (sig > perEmotion[em]) perEmotion[em] = sig;
      });
    });
    ALL_EMOTIONS.forEach(em => {
      maxMap[em] += perEmotion[em];
    });
  });
  return maxMap;
}

export const MAX_SIGNALS = computeMaxSignals();

// ─── Score Calculation ─────────────────────────────────────────────────────────
export interface EmotionScore {
  emotion: Emotion;
  signals: number;
  maxSignals: number;
  percentage: number;
  level: 'Low' | 'Moderate' | 'Strong';
}

export function scoreAnswers(mcAnswers: Record<number, string>): EmotionScore[] {
  const accumulated: Record<Emotion, number> = { Fear: 0, Greed: 0, Ego: 0, Impatience: 0, Hope: 0 };

  QUESTIONS.forEach(q => {
    if (q.type !== 'mc') return;
    const mcQ = q as MCQuestion;
    const chosen = mcAnswers[q.id];
    if (!chosen) return;
    const opt = mcQ.options.find(o => o.value === chosen);
    if (!opt) return;
    ALL_EMOTIONS.forEach(em => {
      accumulated[em] += opt.signals[em] ?? 0;
    });
  });

  return ALL_EMOTIONS.map(em => {
    const signals = accumulated[em];
    const maxSignals = MAX_SIGNALS[em];
    const percentage = maxSignals > 0 ? Math.round((signals / maxSignals) * 100) : 0;
    let level: 'Low' | 'Moderate' | 'Strong';
    if (percentage >= 60) level = 'Strong';
    else if (percentage >= 35) level = 'Moderate';
    else level = 'Low';
    return { emotion: em, signals, maxSignals, percentage, level };
  });
}

// ─── Cross-Reference Logic ─────────────────────────────────────────────────────
export type CrossRefConclusion = 'CONFIRMED' | 'DORMANT' | 'SITUATIONAL' | 'CLEAR';

export interface CrossRefResult {
  emotion: Emotion;
  iChingFound: boolean;   // Step 1 I Ching flag for this emotion
  questionnaireActive: boolean; // Step 2 score >= 35%
  conclusion: CrossRefConclusion;
  conclusionLabel: string;
  conclusionDetail: string;
  score: EmotionScore;
}

// Map Step 1 emotion flag names to Step 2 emotions
const EMOTION_ALIASES: Record<string, Emotion> = {
  'Fear': 'Fear',
  'Extreme Fear': 'Fear',
  'Greed': 'Greed',
  'Extreme Greed': 'Greed',
  'Ego': 'Ego',
  'Deep Ego': 'Ego',
  'Impatience': 'Impatience',
  'Hope': 'Hope',
};

const CONCLUSION_CONFIG: Record<CrossRefConclusion, { label: string; detail: string; color: string }> = {
  CONFIRMED: {
    label: 'CONFIRMED — Core Attitude Problem',
    detail: 'Pattern exists in inborn design AND is currently active. Discipline alone will not fix this. Requires energy regulation and awareness of inborn design.',
    color: 'red',
  },
  DORMANT: {
    label: 'DORMANT — Pattern Present, Not Firing',
    detail: 'The pattern exists in their design but is not currently active. Monitor — it may activate under stress or different market conditions.',
    color: 'amber',
  },
  SITUATIONAL: {
    label: 'SITUATIONAL — Not Inborn, Currently Active',
    detail: 'The problem is real but NOT from their inborn design. Fixable with simple disciplinary measures and structured habit change.',
    color: 'blue',
  },
  CLEAR: {
    label: 'CLEAR — No Issue Detected',
    detail: 'No signal from either I Ching design or current behaviour. No intervention needed for this emotion.',
    color: 'green',
  },
};

export function crossReference(
  emotionScores: EmotionScore[],
  step1EmotionNames: string[] // emotion names from Step 1 analyseEmotions()
): CrossRefResult[] {
  // Build set of Step 1 emotions mapped to canonical names
  const step1Set = new Set<Emotion>();
  step1EmotionNames.forEach(name => {
    const mapped = EMOTION_ALIASES[name];
    if (mapped) step1Set.add(mapped);
  });

  return emotionScores.map(score => {
    const iChingFound = step1Set.has(score.emotion);
    const questionnaireActive = score.level === 'Moderate' || score.level === 'Strong';

    let conclusion: CrossRefConclusion;
    if (iChingFound && questionnaireActive) conclusion = 'CONFIRMED';
    else if (iChingFound && !questionnaireActive) conclusion = 'DORMANT';
    else if (!iChingFound && questionnaireActive) conclusion = 'SITUATIONAL';
    else conclusion = 'CLEAR';

    const config = CONCLUSION_CONFIG[conclusion];

    return {
      emotion: score.emotion,
      iChingFound,
      questionnaireActive,
      conclusion,
      conclusionLabel: config.label,
      conclusionDetail: config.detail,
      score,
    };
  });
}

export { CONCLUSION_CONFIG };

// ─── Step 2 Full Result ────────────────────────────────────────────────────────
export interface Step2Result {
  traderName: string;
  traderDob: string;
  completedAt: string;
  // Open text answers (Q1, Q4, Q5, Q6, Q17, Q18)
  openAnswers: Record<number, string>;
  // Select answer (Q2)
  selectAnswers: Record<number, string>;
  // Scale answer (Q3)
  scaleAnswers: Record<number, number>;
  // MC answers (Q7–Q16)
  mcAnswers: Record<number, string>;
  // Scored results
  emotionScores: EmotionScore[];
  // Cross-reference with Step 1
  crossRef: CrossRefResult[];
  // Q16 awareness level
  awarenessLevel: string;
}

export function buildStep2Result(
  traderName: string,
  traderDob: string,
  openAnswers: Record<number, string>,
  selectAnswers: Record<number, string>,
  scaleAnswers: Record<number, number>,
  mcAnswers: Record<number, string>,
  step1EmotionNames: string[]
): Step2Result {
  const emotionScores = scoreAnswers(mcAnswers);
  const crossRef = crossReference(emotionScores, step1EmotionNames);

  // Q16 awareness mapping
  const awarenessMap: Record<string, string> = {
    A: 'Still in Strategy Dead Loop — believes the problem is mostly knowledge/strategy',
    B: 'Leaning toward Strategy Loop — more knowledge than emotion',
    C: 'Transitioning — recognises both knowledge and emotional factors equally',
    D: 'Aware — beginning to recognise emotional patterns as the primary issue',
    E: 'Fully Aware — knows what to do but cannot execute consistently (ready for Loop 3 work)',
  };
  const awarenessLevel = awarenessMap[mcAnswers[16] ?? ''] ?? 'Not answered';

  return {
    traderName,
    traderDob,
    completedAt: new Date().toISOString(),
    openAnswers,
    selectAnswers,
    scaleAnswers,
    mcAnswers,
    emotionScores,
    crossRef,
    awarenessLevel,
  };
}

/**
 * Recompute crossRef on an existing Step2Result using known step1 emotion names.
 * Call this after the I Ching profile has been computed from the DOB.
 */
export function applyStep1CrossRef(result: Step2Result, step1EmotionNames: string[]): Step2Result {
  const crossRef = crossReference(result.emotionScores, step1EmotionNames);
  return { ...result, crossRef };
}
